import React, { useEffect, useRef, useState } from 'react';
import { fetchWrapper } from 'utils/apiFetch';
import { useSession } from 'hooks/useSession';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  '₹\u00a0' + (parseFloat(n) || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtN = (n) => (parseInt(n) || 0).toLocaleString('en-IN');

const SERVICE_COLORS = {
  'Wallet Settlement': '#1d9e75',
  'AEPS':             '#185fa5',
  'DMT':              '#d85a30',
  'Recharge':         '#639922',
  'MATM':             '#534ab7',
  'BBPS':             '#ba7517',
  'CMS':              '#378add',
  'PAN Card':         '#a32d2d',
  'Insurance':        '#0c447c',
  'Travel':           '#854f0b',
  'Fastag':           '#3b6d11',
  'LIC':              '#3c3489',
};
const getColor = (name) => SERVICE_COLORS[name] || '#888780';

const CARD_PALETTES = {
  emerald:  { bg: 'linear-gradient(135deg,#0f6e56 0%,#1d9e75 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#5dcaa5' },
  sapphire: { bg: 'linear-gradient(135deg,#0c447c 0%,#185fa5 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#85b7eb' },
  violet:   { bg: 'linear-gradient(135deg,#3c3489 0%,#534ab7 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#afa9ec' },
  amber:    { bg: 'linear-gradient(135deg,#854f0b 0%,#ba7517 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#fac775' },
  coral:    { bg: 'linear-gradient(135deg,#993c1d 0%,#d85a30 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#f0997b' },
  forest:   { bg: 'linear-gradient(135deg,#27500a 0%,#639922 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#c0dd97' },
  rose:     { bg: 'linear-gradient(135deg,#791f1f 0%,#a32d2d 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#f09595' },
  sky:      { bg: 'linear-gradient(135deg,#042c53 0%,#378add 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#b5d4f4' },
  clock:    { bg: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 100%)', text: '#fff', sub: 'rgba(255,255,255,.5)', icon: 'rgba(255,255,255,.15)', accent: '#5dcaa5' },
  pink:     { bg: 'linear-gradient(135deg,#72243e 0%,#993556 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#f4c0d1' },
  indigo:   { bg: 'linear-gradient(135deg,#26215c 0%,#3c3489 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#afa9ec' },
  teal2:    { bg: 'linear-gradient(135deg,#085041 0%,#0f6e56 100%)', text: '#fff', sub: 'rgba(255,255,255,.7)', icon: 'rgba(255,255,255,.25)', accent: '#5dcaa5' },
};

const PILL_PALETTES = {
  teal:   { bg: 'linear-gradient(135deg,#0f6e56 0%,#1d9e75 100%)', shimmer: '#5dcaa5' },
  blue:   { bg: 'linear-gradient(135deg,#0c447c 0%,#185fa5 100%)', shimmer: '#85b7eb' },
  green:  { bg: 'linear-gradient(135deg,#27500a 0%,#639922 100%)', shimmer: '#c0dd97' },
  red:    { bg: 'linear-gradient(135deg,#501313 0%,#a32d2d 100%)', shimmer: '#f09595' },
  violet: { bg: 'linear-gradient(135deg,#3c3489 0%,#534ab7 100%)', shimmer: '#afa9ec' },
  amber:  { bg: 'linear-gradient(135deg,#633806 0%,#ba7517 100%)', shimmer: '#fac775' },
};

// ── Role definitions ──────────────────────────────────────────────────────────
// Maps role_id from API → label and style
// API role_id: 6=Main Admin, 1=Admin, 2=White Label, 3=Master Dist, 4=Dist, 5=Retailer
const ROLE_META = {
  6:  { label: 'Main Admin',         color: '#0c447c', bg: '#b5d4f422', icon: '⭐' },
  1:  { label: 'Admin',              color: '#185fa5', bg: '#85b7eb22', icon: '🛡️' },
  2:  { label: 'White Label',        color: '#1d9e75', bg: '#5dcaa522', icon: '🏷️' },
  3:  { label: 'Master Distributor', color: '#d85a30', bg: '#f0997b22', icon: '📦' },
  4:  { label: 'Distributor',        color: '#ba7517', bg: '#fac77522', icon: '🚚' },
  5:  { label: 'Retailer',           color: '#639922', bg: '#c0dd9722', icon: '🏪' },
};
const getRoleMeta = (id) => ROLE_META[id] || { label: `Role ${id}`, color: '#888780', bg: '#88878022', icon: '👤' };

// ── Tab definitions ───────────────────────────────────────────────────────────
// roles: null = all, array = specific role_ids
const ALL_TABS = [
  { id: 'overview',   label: 'Overview',    icon: '📊', roles: null },
  { id: 'wallet',     label: 'Wallet',      icon: '💼', roles: [1, 2, 3, 4, 5, 6] },
  { id: 'services',   label: 'Services',    icon: '⚙️', roles: [1, 2, 3, 4, 5, 6] },
  { id: 'recharge',   label: 'Recharge',    icon: '📶', roles: null },
  { id: 'commission', label: 'Commission',  icon: '💹', roles: [1, 2, 3, 4, 5, 6] },
  { id: 'settings',   label: 'Settings',    icon: '⚙️', roles: [1, 2, 3, 4, 5, 6] },
];

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  .mq-dash * { box-sizing: border-box; margin: 0; padding: 0; }
  .mq-dash { font-family: 'Plus Jakarta Sans', sans-serif; }

  @keyframes mqFadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes mqSlideIn {
    from { opacity: 0; transform: translateX(-10px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes mqBarGrow {
    from { width: 0; }
    to   { width: var(--bar-w); }
  }
  @keyframes mqPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.3; }
  }
  @keyframes mqShimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes mqTabSlide {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mq-fade-up  { animation: mqFadeUp .5s cubic-bezier(.22,1,.36,1) both; }
  .mq-slide-in { animation: mqSlideIn .38s ease both; }
  .mq-tab-content { animation: mqTabSlide .3s ease both; }

  .mq-stat-card {
    border-radius: 18px;
    position: relative;
    overflow: hidden;
    padding: 20px 22px 18px;
    transition: transform .22s, box-shadow .22s;
    box-shadow: 0 4px 24px rgba(0,0,0,.13);
  }
  .mq-stat-card:hover {
    transform: translateY(-4px) scale(1.01);
    box-shadow: 0 12px 36px rgba(0,0,0,.22);
  }
  .mq-stat-card::before {
    content: '';
    position: absolute;
    top: -30px; right: -30px;
    width: 120px; height: 120px;
    border-radius: 50%;
    background: rgba(255,255,255,.08);
  }
  .mq-stat-card::after {
    content: '';
    position: absolute;
    bottom: -20px; left: -20px;
    width: 80px; height: 80px;
    border-radius: 50%;
    background: rgba(255,255,255,.05);
  }

  .mq-pill {
    background: #fff;
    border-radius: 14px;
    padding: 13px 15px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: transform .2s, box-shadow .2s;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
  }
  .mq-pill:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,.10);
  }

  .mq-card {
    background: #fff;
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 2px 14px rgba(0,0,0,.06);
    transition: transform .2s, box-shadow .2s;
  }
  .mq-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 28px rgba(0,0,0,.10);
  }

  .mq-tr { transition: background .15s; }
  .mq-tr:hover { background: #f7f8fa !important; }

  .mq-live-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #1d9e75;
    animation: mqPulse 2s infinite;
    box-shadow: 0 0 0 3px rgba(29,158,117,.2);
  }

  .mq-bar-fill {
    height: 100%;
    border-radius: 999px;
    animation: mqBarGrow .9s cubic-bezier(.22,1,.36,1) both;
    animation-delay: var(--bar-delay, 0s);
    width: var(--bar-w, 0%);
  }

  .mq-section-head {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 14px;
  }
  .mq-section-bar {
    width: 4px; height: 16px;
    border-radius: 2px;
    flex-shrink: 0;
  }
  .mq-section-title {
    font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: .12em;
    color: #888;
  }
  .mq-section-line {
    flex: 1; height: 0.5px; background: rgba(0,0,0,.07);
  }

  .mq-txn-badge {
    border-radius: 999px; padding: 3px 10px;
    font-size: 11px; font-family: 'JetBrains Mono', monospace; font-weight: 500;
  }

  .mq-recharge-half {
    flex: 1; min-width: 0;
    border-radius: 12px; padding: 14px;
  }

  .mq-header-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(40px);
    pointer-events: none;
    opacity: .18;
  }

  .mq-tab-nav {
    display: flex;
    gap: 4px;
    background: #fff;
    border-radius: 16px;
    padding: 6px;
    box-shadow: 0 2px 14px rgba(0,0,0,.07);
    margin-bottom: 22px;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .mq-tab-nav::-webkit-scrollbar { display: none; }

  .mq-tab-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 16px;
    border-radius: 11px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #888;
    white-space: nowrap;
    transition: all .2s;
    flex-shrink: 0;
  }
  .mq-tab-btn:hover:not(.active) {
    background: #f5f6f8;
    color: #444;
  }
  .mq-tab-btn.active {
    background: linear-gradient(135deg,#0f6e56,#1d9e75);
    color: #fff;
    box-shadow: 0 4px 14px rgba(29,158,117,.35);
  }
  .mq-tab-icon { font-size: 15px; }

  .mq-role-badge {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 6px 14px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    border: 1.5px solid;
  }

  .mq-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 14px;
    margin-bottom: 20px;
  }
  .mq-info-item {
    background: #fff;
    border-radius: 14px;
    padding: 16px 18px;
    box-shadow: 0 2px 12px rgba(0,0,0,.06);
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .mq-info-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: #aaa;
  }
  .mq-info-value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 15px;
    font-weight: 500;
    color: #1a1a1a;
  }

  .mq-comm-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px;
    border-bottom: 0.5px solid rgba(0,0,0,.05);
    transition: background .15s;
  }
  .mq-comm-row:last-child { border-bottom: none; }
  .mq-comm-row:hover { background: #f7f8fa; }

  .mq-status-dot {
    width: 10px; height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .mq-status-dot.online  { background: #1d9e75; box-shadow: 0 0 0 3px rgba(29,158,117,.25); animation: mqPulse 2s infinite; }
  .mq-status-dot.warning { background: #ba7517; box-shadow: 0 0 0 3px rgba(186,117,23,.25); }
  .mq-status-dot.offline { background: #a32d2d; box-shadow: 0 0 0 3px rgba(163,45,45,.25); }
`;

// ── Sub-components (unchanged from original) ──────────────────────────────────

function StatCard({ label, value, icon, palette = 'emerald', sub, delay = 0 }) {
  const p = CARD_PALETTES[palette];
  return (
    <div className="mq-stat-card mq-fade-up" style={{ background: p.bg, animationDelay: `${delay}s` }}>
      <div style={{ position: 'absolute', top: 16, right: 18, fontSize: 22, color: p.icon, zIndex: 1 }}>{icon}</div>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: p.sub, marginBottom: 10, zIndex: 1, position: 'relative' }}>{label}</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 17, fontWeight: 500, color: p.text, lineHeight: 1.1, marginBottom: 6, zIndex: 1, position: 'relative' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: p.sub, zIndex: 1, position: 'relative' }}>{sub}</div>}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)`, backgroundSize: '200% 100%', animation: 'mqShimmer 2.5s linear infinite' }} />
    </div>
  );
}

function ClockCard({ currentTime, delay = 0 }) {
  const p = CARD_PALETTES.clock;
  return (
    <div className="mq-stat-card mq-fade-up" style={{ background: p.bg, animationDelay: `${delay}s` }}>
      <div style={{ position: 'absolute', top: 16, right: 18, fontSize: 22, color: 'rgba(255,255,255,.2)' }}>🕐</div>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.5)', marginBottom: 10 }}>Live Clock</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 500, color: p.accent, letterSpacing: 1, marginBottom: 4 }}>
        {currentTime.toLocaleTimeString('en-IN')}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,.45)' }}>
        {currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${p.accent}, transparent)`, backgroundSize: '200% 100%', animation: 'mqShimmer 2.5s linear infinite' }} />
    </div>
  );
}

function SummaryPill({ label, value, icon, palette = 'teal', delay = 0 }) {
  const p = PILL_PALETTES[palette];
  return (
    <div className="mq-pill mq-fade-up" style={{ animationDelay: `${delay}s`, background: p.bg, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.15)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 70, height: 70, borderRadius: '50%', background: 'rgba(255,255,255,.08)', pointerEvents: 'none' }} />
      <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0, position: 'relative' }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.65)', marginBottom: 3 }}>{label}</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${p.shimmer}, transparent)`, backgroundSize: '200% 100%', animation: 'mqShimmer 2.5s linear infinite' }} />
    </div>
  );
}

function SectionHead({ children, color = '#1d9e75', delay = 0 }) {
  return (
    <div className="mq-section-head mq-fade-up" style={{ animationDelay: `${delay}s` }}>
      <div className="mq-section-bar" style={{ background: color }} />
      <span className="mq-section-title">{children}</span>
      <div className="mq-section-line" />
    </div>
  );
}

function Td({ children, color, mono = true }) {
  return (
    <td style={{ padding: '11px 13px', color: color || '#1a1a1a', fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {children}
    </td>
  );
}

function RechargeStatsCard({ recharge, delay = 0 }) {
  // API keys: success_count, success_amount, failed_count, failed_amount
  const r = recharge || {};
  return (
    <div className="mq-stat-card mq-fade-up" style={{ background: 'linear-gradient(135deg,#27500a 0%,#3b6d11 60%,#639922 100%)', animationDelay: `${delay}s` }}>
      <div style={{ position: 'absolute', top: 16, right: 18, fontSize: 22, color: 'rgba(255,255,255,.2)' }}>📶</div>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.6)', marginBottom: 12 }}>Recharge Today</div>
      <div style={{ display: 'flex', gap: 8, position: 'relative', zIndex: 1 }}>
        <div className="mq-recharge-half" style={{ background: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.18)' }}>
          <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,.6)', marginBottom: 5 }}>✅ Success</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 500, color: '#fff', lineHeight: 1, marginBottom: 3 }}>{fmtN(r.success_count)}</div>
          <div style={{ fontSize: 10, color: '#c0dd97', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(r.success_amount)}</div>
        </div>
        <div className="mq-recharge-half" style={{ background: 'rgba(163,45,45,.25)', border: '1px solid rgba(240,149,149,.25)' }}>
          <div style={{ fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'rgba(255,255,255,.6)', marginBottom: 5 }}>❌ Failed</div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, fontWeight: 500, color: '#fff', lineHeight: 1, marginBottom: 3 }}>{fmtN(r.failed_count)}</div>
          <div style={{ fontSize: 10, color: '#f7c1c1', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(r.failed_amount)}</div>
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #c0dd97, transparent)', backgroundSize: '200% 100%', animation: 'mqShimmer 2.5s linear infinite' }} />
    </div>
  );
}

function DonutChart({ cr, dr, net }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || typeof window.Chart === 'undefined') return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new window.Chart(canvasRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Comm CR', 'Comm DR', 'Net Comm'],
        datasets: [{ data: [parseFloat(cr) || 0, parseFloat(dr) || 0, parseFloat(net) || 0], backgroundColor: ['#1d9e75', '#a32d2d', '#534ab7'], borderWidth: 4, borderColor: '#fff', hoverOffset: 7 }],
      },
      options: { responsive: true, maintainAspectRatio: false, cutout: '72%', plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => ' ' + fmt(c.parsed) } } } },
    });
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [cr, dr, net]);
  return (
    <div style={{ position: 'relative', height: 150, width: '100%', display: 'flex', justifyContent: 'center' }}>
      <canvas ref={canvasRef} role="img" aria-label="Commission split chart" />
    </div>
  );
}

function ServiceBarChart({ services }) {
  const maxAmt = Math.max(...services.map((x) => x.amount || 0), 1);
  return (
    <div>
      {services.map((item, i) => {
        const pct   = ((item.amount || 0) / maxAmt * 100).toFixed(1);
        const color = getColor(item.service);
        return (
          <div key={i} className="mq-slide-in" style={{ marginBottom: 12, animationDelay: `${i * 0.06}s` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12, color: '#444', display: 'flex', alignItems: 'center', gap: 7, fontWeight: 500 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', boxShadow: `0 0 0 3px ${color}22` }} />
                {item.service}
              </span>
              <span style={{ fontSize: 11, color: '#777', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(item.amount)}</span>
            </div>
            <div style={{ height: 7, background: '#f0f0f0', borderRadius: 999, overflow: 'hidden' }}>
              <div className="mq-bar-fill" style={{ '--bar-w': `${pct}%`, '--bar-delay': `${i * 0.06}s`, background: color, boxShadow: `0 0 8px ${color}55` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MaintenanceCard({ maintenance, onToggle, delay = 0 }) {
  const active = maintenance.is_active;
  return (
    <div className="mq-stat-card mq-fade-up" style={{
      background: active ? 'linear-gradient(135deg,#501313 0%,#a32d2d 100%)' : 'linear-gradient(135deg,#042c53 0%,#185fa5 100%)',
      animationDelay: `${delay}s`,
    }}>
      <div style={{ position: 'absolute', top: 16, right: 18, fontSize: 22, color: 'rgba(255,255,255,.2)' }}>🔧</div>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.55)', marginBottom: 10 }}>Maintenance Mode</div>
      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 500, color: active ? '#f7c1c1' : '#b5d4f4', marginBottom: 12 }}>
        {active ? '● ACTIVE' : '○ INACTIVE'}
      </div>
      <button onClick={() => onToggle(!active)} style={{ fontSize: 11, padding: '6px 16px', borderRadius: 8, cursor: 'pointer', border: '1px solid rgba(255,255,255,.25)', fontWeight: 600, background: 'rgba(255,255,255,.15)', color: '#fff', backdropFilter: 'blur(4px)', transition: 'background .2s' }}>
        {active ? 'Disable' : 'Enable'}
      </button>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: active ? 'linear-gradient(90deg, transparent, #f09595, transparent)' : 'linear-gradient(90deg, transparent, #85b7eb, transparent)', backgroundSize: '200% 100%', animation: 'mqShimmer 2.5s linear infinite' }} />
    </div>
  );
}

// ── Tab Pages ─────────────────────────────────────────────────────────────────

function OverviewTab({ d, recharge, chartReady, currentTime, maintenance, onToggleMaintenance, roleId }) {
  // ── API field mapping ──────────────────────────────────────────────────────
  // d.summary keys from API:
  //   total_entries, total_amount, total_in_commission, total_out_commission,
  //   total_net_commission, total_in_charge, total_out_charge, total_net_charge
  // d.wallets keys (from fixed DashBoardAPI):
  //   wallet_amount, main_wallet, credit_wallet, hold_amount
  // d.downline_wallets: { admin:{count,wallet_total}, white_label:{...}, ... }
  // d.member_stats: { admin:{total,new_today}, white_label:{...}, ... }
  // d.platform_totals: { Admin:N, "White Label":N, ... }  (Main Admin only)
  // d.txn_today: { success:{count,amount}, failed:{count,amount}, pending:{count,amount} }
  // d.pending_load_requests: N

  const s        = d?.summary || {};
  const services = d?.services || [];
  const wallets  = d?.wallets  || {};

  // Wallet fields — new API uses d.wallets.wallet_amount, d.wallets.main_wallet
  // Old API had d.wallet_amount, d.admin_wallet, d.retailer_wallet, d.whitelabel_wallet
  // Support both shapes so nothing breaks during migration
  const myWallet       = wallets.wallet_amount  ?? d?.wallet_amount       ?? 0;
  const myMainWallet   = wallets.main_wallet    ?? 0;
  const myCreditWallet = wallets.credit_wallet  ?? 0;
  const myVerificationWallet = wallets.verification_wallet ?? 0;  // ← add
  const myUpiWallet          = wallets.upi_wallet          ?? 0;  // ← add

  // Downline wallet totals (non-retailer roles)
  const dlWallets = d?.downline_wallets || {};

  // Summary commission — API sends total_net_commission
  const netComm  = s.total_net_commission ?? 0;
  const totalAmt = s.total_amount ?? 0;

  // Role visibility
  const isMainAdmin = roleId === 6;
  const isAdmin     = roleId === 1;
  const showWalletBreakdown = isMainAdmin || isAdmin;

  const totals = services.reduce((acc, item) => ({
    entries:        acc.entries        + (item.entries        || 0),
    amount:         acc.amount         + (item.amount         || 0),
    in_commission:  acc.in_commission  + (item.in_commission  || 0),
    out_commission: acc.out_commission + (item.out_commission || 0),
    net_commission: acc.net_commission + (item.net_commission || 0),
    in_charge:      acc.in_charge      + (item.in_charge      || 0),
    out_charge:     acc.out_charge     + (item.out_charge     || 0),
    net_charge:     acc.net_charge     + (item.net_charge     || 0),
  }), { entries:0, amount:0, in_commission:0, out_commission:0, net_commission:0, in_charge:0, out_charge:0, net_charge:0 });

  return (
    <div className="mq-tab-content">
      {/* ── Stat Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(195px, 1fr))', gap: 14, marginBottom: 26 }}>
        {/* My wallet */}
        <StatCard label="Wallet Balance"    value={fmt(myWallet)}          icon="💼" palette="emerald"  sub="Available funds"          delay={0.05} />
        {/* Main wallet (commission wallet) */}
        {myMainWallet > 0 && (
          <StatCard label="Main Wallet"     value={fmt(myMainWallet)}      icon="🏦" palette="sapphire" sub="Commission wallet"         delay={0.07} />
        )}
        {/* Credit wallet */}
        {myCreditWallet > 0 && (
          <StatCard label="Credit Wallet"   value={fmt(myCreditWallet)}    icon="💳" palette="indigo"   sub="Credit balance"           delay={0.09} />
        )}
        {myVerificationWallet > 0 && (
  <StatCard label="Verification Wallet" value={fmt(myVerificationWallet)} icon="✅" palette="forest"  sub="Verification balance" delay={0.11} />
)}
{myUpiWallet > 0 && (
  <StatCard label="UPI Wallet"          value={fmt(myUpiWallet)}          icon="📲" palette="pink"    sub="UPI balance"          delay={0.13} />
)}


        {/* Downline pool totals — shown when available */}
        {showWalletBreakdown && dlWallets.white_label && (
          <StatCard label="Whitelabel Pool" value={fmt(dlWallets.white_label.wallet_total)}  icon="🏷️" palette="violet" sub={`${dlWallets.white_label.count} members`} delay={0.11} />
        )}
        {showWalletBreakdown && dlWallets.retailer && (
          <StatCard label="Retailer Pool"   value={fmt(dlWallets.retailer.wallet_total)}     icon="🏪" palette="amber"  sub={`${dlWallets.retailer.count} members`}   delay={0.14} />
        )}

        <ClockCard currentTime={currentTime} delay={0.17} />
        <StatCard label="Today's Earning"   value={fmt(netComm)}           icon="📈" palette="forest"   sub="Net commission today"     delay={0.20} />
        <StatCard label="Today's Sales"     value={fmt(totalAmt)}          icon="💹" palette="sky"      sub="Total txn amount"         delay={0.23} />
        <RechargeStatsCard recharge={recharge} delay={0.26} />
        {(isAdmin || isMainAdmin) && (
          <MaintenanceCard maintenance={maintenance} onToggle={onToggleMaintenance} delay={0.29} />
        )}
      </div>

      {/* ── Member stats (non-retailer) ── */}
      {d?.member_stats && Object.keys(d.member_stats).length > 0 && (
        <>
          <SectionHead color="#185fa5" delay={0.08}>Member Stats — Today</SectionHead>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, marginBottom: 22 }}>
            {Object.entries(d.member_stats).map(([key, val], i) => (
              <div key={key} className="mq-pill mq-fade-up" style={{ animationDelay: `${0.08 + i * 0.04}s`, flexDirection: 'column', gap: 4, alignItems: 'flex-start', background: '#fff', padding: '14px 16px' }}>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.1em', color: '#aaa' }}>
                  {key.replace(/_/g, ' ')}
                </span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 600, color: '#185fa5' }}>{fmtN(val.total)}</span>
                <span style={{ fontSize: 10, color: '#1d9e75' }}>+{val.new_today} today</span>
              </div>
            ))}
          </div>
        </>
      )}
{d?.verification_cost?.today > 0 && (
  <StatCard
    label="Verification Cost Today"
    value={fmt(d.verification_cost.today)}
    icon="🔍"
    palette="rose"
    sub={`${fmtN(d.verification_cost.today_count)} verifications`}
    delay={0.32}
  />
)}
      {/* ── Pending requests badge ── */}
      {d?.pending_load_requests > 0 && (
        <div className="mq-fade-up" style={{ background: 'linear-gradient(135deg,#854f0b,#ba7517)', borderRadius: 14, padding: '14px 20px', marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
            🔔 {d.pending_load_requests} pending wallet load request{d.pending_load_requests > 1 ? 's' : ''}
          </span>
          <a href="/load_wallet" style={{ background: '#fff', color: '#854f0b', borderRadius: 8, padding: '6px 16px', fontWeight: 700, fontSize: 12, textDecoration: 'none' }}>
            Review →
          </a>
        </div>
      )}

      {/* ── Today's txn status summary ── */}
      {d?.txn_today && (
        <>
          <SectionHead color="#534ab7" delay={0.10}>Today's Transactions</SectionHead>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 22 }}>
            {[
              { label: 'Success', data: d.txn_today.success, color: '#1d9e75', icon: '✅' },
              { label: 'Failed',  data: d.txn_today.failed,  color: '#a32d2d', icon: '❌' },
              { label: 'Pending', data: d.txn_today.pending, color: '#ba7517', icon: '⏳' },
            ].map(({ label, data, color, icon }) => (
              <div key={label} style={{ background: '#fff', borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 12px rgba(0,0,0,.06)', borderLeft: `4px solid ${color}` }}>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 6, fontWeight: 600 }}>{icon} {label}</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color, marginBottom: 3 }}>{fmtN(data?.count)}</div>
                <div style={{ fontSize: 11, color: '#aaa', fontFamily: "'JetBrains Mono', monospace" }}>{fmt(data?.amount)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── Summary Pills ── */}
      <SectionHead color="#534ab7" delay={0.10}>Summary Totals</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 26 }}>
        <SummaryPill label="Total Txns"       value={fmtN(s.total_entries)}        icon="🔢" palette="teal"   delay={0.10} />
        <SummaryPill label="Total Amount"     value={fmt(s.total_amount)}           icon="💰" palette="blue"   delay={0.13} />
        <SummaryPill label="Commission CR"    value={fmt(s.total_in_commission)}    icon="📥" palette="green"  delay={0.16} />
        <SummaryPill label="Commission DR"    value={fmt(s.total_out_commission)}   icon="📤" palette="red"    delay={0.19} />
        <SummaryPill label="Net Commission"   value={fmt(s.total_net_commission)}   icon="⬆️" palette="teal"  delay={0.22} />
        <SummaryPill label="Net Charge"       value={fmt(s.total_net_charge)}       icon="📊" palette="violet" delay={0.25} />
        <SummaryPill label="Recharge Success" value={`${fmtN(recharge.success_count)} · ${fmt(recharge.success_amount)}`} icon="✅" palette="green" delay={0.28} />
        <SummaryPill label="Recharge Failed"  value={`${fmtN(recharge.failed_count)} · ${fmt(recharge.failed_amount)}`}  icon="❌" palette="red"   delay={0.31} />
      </div>

      {/* ── Charts ── */}
      <SectionHead color="#185fa5" delay={0.15}>Service Analytics</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 26 }}>
        <div className="mq-card mq-fade-up" style={{ padding: '20px 22px', animationDelay: '.2s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#0f6e56,#1d9e75)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff' }}>📊</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Amount by Service</div>
              <div style={{ fontSize: 10, color: '#aaa' }}>Transaction volume breakdown</div>
            </div>
          </div>
          {services.length > 0 ? <ServiceBarChart services={services} /> : <div style={{ color: '#bbb', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>No data</div>}
        </div>
        <div className="mq-card mq-fade-up" style={{ padding: '20px 22px', animationDelay: '.24s' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg,#3c3489,#534ab7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff' }}>🔵</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>Commission Split</div>
              <div style={{ fontSize: 10, color: '#aaa' }}>CR vs DR vs Net</div>
            </div>
          </div>
          {chartReady && <DonutChart cr={s.total_in_commission} dr={s.total_out_commission} net={s.total_net_commission} />}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 14 }}>
            {[
              { label: 'Comm CR',  val: s.total_in_commission,  color: '#1d9e75' },
              { label: 'Comm DR',  val: s.total_out_commission, color: '#a32d2d' },
              { label: 'Net Comm', val: s.total_net_commission, color: '#534ab7' },
            ].map((item) => (
              <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555', fontWeight: 500 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0, boxShadow: `0 0 0 2px ${item.color}33` }} />
                {item.label}: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: item.color }}>{fmt(item.val)}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Service Table ── */}
      <SectionHead color="#ba7517" delay={0.20}>Service Summary</SectionHead>
      <div className="mq-card mq-fade-up" style={{ overflow: 'hidden', animationDelay: '.25s' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 125 }} /><col style={{ width: 68 }} /><col style={{ width: 105 }} />
              <col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} />
              <col style={{ width: 90 }} /><col style={{ width: 90 }} /><col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr style={{ background: 'linear-gradient(90deg,#f7f8fa,#eef0f3)', borderBottom: '1px solid rgba(0,0,0,.07)' }}>
                {['Service','Txns','Amount','Comm CR','Comm DR','Net Comm','Chg CR','Chg DR','Net Chg'].map((h) => (
                  <th key={h} style={{ padding: '12px 13px', textAlign: 'left', fontSize: 9, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#777', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#bbb', padding: '40px 0', fontSize: 13 }}>No service data for today</td></tr>
              ) : services.map((item, idx) => {
                const color = getColor(item.service);
                return (
                  <tr key={idx} className="mq-tr" style={{ borderBottom: '0.5px solid rgba(0,0,0,.05)', background: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <Td mono={false}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0, display: 'inline-block', boxShadow: `0 0 0 3px ${color}22` }} />
                        {item.service}
                      </span>
                    </Td>
                    <td style={{ padding: '11px 13px' }}>
                      <span className="mq-txn-badge" style={{ background: `${color}18`, color, border: `1px solid ${color}33` }}>{fmtN(item.entries)}</span>
                    </td>
                    <Td>{fmt(item.amount)}</Td>
                    <Td color="#0f6e56">{fmt(item.in_commission)}</Td>
                    <Td color={item.out_commission ? '#a32d2d' : '#ccc'}>{item.out_commission ? fmt(item.out_commission) : '₹\u00a00.00'}</Td>
                    <Td color="#0f6e56">{fmt(item.net_commission)}</Td>
                    <Td color="#185fa5">{fmt(item.in_charge)}</Td>
                    <Td color={item.out_charge ? '#a32d2d' : '#ccc'}>{item.out_charge ? fmt(item.out_charge) : '₹\u00a00.00'}</Td>
                    <Td color="#185fa5">{fmt(item.net_charge)}</Td>
                  </tr>
                );
              })}
            </tbody>
            {services.length > 0 && (
              <tfoot style={{ background: 'linear-gradient(90deg,#e8f7f2,#edf4fb)', borderTop: '2px solid rgba(29,158,117,.15)' }}>
                <tr>
                  <td style={{ padding: '12px 13px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#0f6e56' }}>Total</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#0f6e56' }}>{fmtN(totals.entries)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 600, color: '#1a1a1a' }}>{fmt(totals.amount)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#0f6e56' }}>{fmt(totals.in_commission)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#a32d2d' }}>{fmt(totals.out_commission)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#0f6e56' }}>{fmt(totals.net_commission)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#185fa5' }}>{fmt(totals.in_charge)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#a32d2d' }}>{fmt(totals.out_charge)}</td>
                  <td style={{ padding: '12px 13px', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 500, color: '#185fa5' }}>{fmt(totals.net_charge)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}

function WalletTab({ d, roleId }) {
  const wallets  = d?.wallets || {};
  const dlWallets = d?.downline_wallets || {};

  // Support both old API shape (d.wallet_amount) and new (d.wallets.wallet_amount)
  const myWallet            = wallets.wallet_amount       ?? d?.wallet_amount ?? 0;
  const myMainWallet        = wallets.main_wallet         ?? 0;
  const myCreditWallet      = wallets.credit_wallet       ?? 0;
  const myHold              = wallets.hold_amount         ?? 0;
  const myVerificationWallet = wallets.verification_wallet ?? 0;  // ← add
  const myUpiWallet          = wallets.upi_wallet          ?? 0;

  const isMainAdmin = roleId === 6;
  const isAdmin     = roleId === 1;
  const showBreakdown = isMainAdmin || isAdmin;

  const s = d?.summary || {};

  return (
    <div className="mq-tab-content">
      <SectionHead color="#1d9e75" delay={0}>My Wallet Balances</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 26 }}>
        <StatCard label="Wallet Balance"   value={fmt(myWallet)}        icon="💼" palette="emerald"  sub="Primary transaction wallet"  delay={0.05} />
        {myMainWallet > 0   && <StatCard label="Main Wallet"    value={fmt(myMainWallet)}    icon="🏦" palette="sapphire" sub="Commission / earning wallet"  delay={0.08} />}
        {myCreditWallet > 0 && <StatCard label="Credit Wallet"  value={fmt(myCreditWallet)}  icon="💳" palette="violet"   sub="Credited balance"             delay={0.11} />}
        {myHold > 0         && <StatCard label="On Hold"        value={fmt(myHold)}          icon="🔒" palette="amber"    sub="Frozen / held amount"         delay={0.14} />}
        {myVerificationWallet > 0 && (
          <StatCard label="Verification Wallet" value={fmt(myVerificationWallet)} icon="✅" palette="forest"  sub="Verification balance" delay={0.17} />
        )}
        {myUpiWallet > 0 && (
          <StatCard label="UPI Wallet"          value={fmt(myUpiWallet)}          icon="📲" palette="pink"    sub="UPI balance"          delay={0.20} />
        )}
       
      </div>

      {/* Downline pool totals */}
      {showBreakdown && Object.keys(dlWallets).length > 0 && (
        <>
          <SectionHead color="#534ab7" delay={0.10}>Downline Wallet Pools</SectionHead>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 26 }}>
            {dlWallets.admin         && <StatCard label="Admin Pool"        value={fmt(dlWallets.admin.wallet_total)}         icon="🛡️" palette="sapphire" sub={`${dlWallets.admin.count} admins`}         delay={0.12} />}
            {dlWallets.white_label   && <StatCard label="White Label Pool"  value={fmt(dlWallets.white_label.wallet_total)}   icon="🏷️" palette="violet"   sub={`${dlWallets.white_label.count} members`}   delay={0.15} />}
            {dlWallets.master_distributor && <StatCard label="Master Dist Pool" value={fmt(dlWallets.master_distributor.wallet_total)} icon="📦" palette="coral" sub={`${dlWallets.master_distributor.count} members`} delay={0.18} />}
            {dlWallets.distributor   && <StatCard label="Distributor Pool"  value={fmt(dlWallets.distributor.wallet_total)}   icon="🚚" palette="amber"    sub={`${dlWallets.distributor.count} members`}   delay={0.21} />}
            {dlWallets.retailer      && <StatCard label="Retailer Pool"     value={fmt(dlWallets.retailer.wallet_total)}      icon="🏪" palette="forest"   sub={`${dlWallets.retailer.count} members`}     delay={0.24} />}
          </div>
        </>
      )}
       {d?.verification_cost && (
  <>
    <SectionHead color="#a32d2d" delay={0.20}>Verification Cost</SectionHead>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 26 }}>
      <StatCard
        label="Total Verification Cost"
        value={fmt(d.verification_cost.total)}
        icon="🔍"
        palette="rose"
        sub={`${fmtN(d.verification_cost.count)} verifications`}
        delay={0.22}
      />
      <StatCard
        label="Today's Verification Cost"
        value={fmt(d.verification_cost.today)}
        icon="📋"
        palette="coral"
        sub={`${fmtN(d.verification_cost.today_count)} verifications today`}
        delay={0.25}
      />
    </div>
  </>
)}

      {/* Movement summary */}
      <SectionHead color="#534ab7" delay={0.10}>Wallet Activity Today</SectionHead>
      <div className="mq-card" style={{ padding: '20px 22px', marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: 'Total Loaded Today',  value: fmt(s.total_amount),          color: '#1d9e75', icon: '⬆️' },
            { label: 'Total Debited Today', value: fmt(s.total_out_commission),   color: '#a32d2d', icon: '⬇️' },
            { label: 'Net Movement',        value: fmt(s.total_net_commission),   color: '#534ab7', icon: '↔️' },
          ].map((item) => (
            <div key={item.label} style={{ textAlign: 'center', padding: '16px 12px', borderRadius: 12, background: '#f8f9fb' }}>
              <div style={{ fontSize: 22, marginBottom: 8 }}>{item.icon}</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 600, color: item.color, marginBottom: 5 }}>{item.value}</div>
              <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em' }}>{item.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: '4px 0' }}>
        <a href="/load_wallet" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, background: 'linear-gradient(135deg,#0f6e56,#1d9e75)', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 14px rgba(29,158,117,.3)' }}>
          💰 Add Fund / Approve Request
        </a>
        <a href="/credit_debit_report" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, background: '#fff', color: '#185fa5', fontWeight: 700, fontSize: 13, textDecoration: 'none', border: '1.5px solid #185fa5', boxShadow: '0 2px 8px rgba(0,0,0,.06)' }}>
          📄 Credit / Debit Report
        </a>
      </div>
    </div>
  );
}

// ServicesTab, RechargeTab, CommissionTab, SettingCard, SettingsTab — unchanged from original
function ServicesTab({ d }) {
  const services = d?.services || [];
  return (
    <div className="mq-tab-content">
      <SectionHead color="#ba7517" delay={0}>Service Performance</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12, marginBottom: 24 }}>
        {services.map((item, i) => {
          const color = getColor(item.service);
          return (
            <div key={i} className="mq-card mq-fade-up" style={{ padding: '16px 18px', animationDelay: `${i * 0.05}s` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, boxShadow: `0 0 0 3px ${color}22`, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a1a' }}>{item.service}</span>
              </div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 17, fontWeight: 600, color, marginBottom: 4 }}>{fmt(item.amount)}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, color: '#aaa' }}>Txns</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#555', fontWeight: 500 }}>{fmtN(item.entries)}</span>
              </div>
              <div style={{ marginTop: 8, height: 4, background: '#f0f0f0', borderRadius: 999 }}>
                <div style={{ height: '100%', background: color, borderRadius: 999, width: `${Math.min((item.entries / 100) * 100, 100)}%`, opacity: .8 }} />
              </div>
            </div>
          );
        })}
        {services.length === 0 && <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#bbb', padding: '40px 0', fontSize: 13 }}>No service data for today</div>}
      </div>
      <SectionHead color="#1d9e75" delay={0.15}>Service Bar Chart</SectionHead>
      <div className="mq-card" style={{ padding: '20px 22px' }}>
        {services.length > 0 ? <ServiceBarChart services={services} /> : <div style={{ color: '#bbb', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>No data</div>}
      </div>
    </div>
  );
}

function RechargeTab({ recharge, chartReady }) {
  const r = recharge || {};
  const total      = (parseInt(r.success_count) || 0) + (parseInt(r.failed_count) || 0);
  const successPct = total ? ((parseInt(r.success_count) || 0) / total * 100).toFixed(1) : '0.0';
  const failPct    = total ? ((parseInt(r.failed_count)  || 0) / total * 100).toFixed(1) : '0.0';
  return (
    <div className="mq-tab-content">
      <SectionHead color="#639922" delay={0}>Recharge Overview</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 26 }}>
        <StatCard label="Success Count"  value={fmtN(r.success_count)}  icon="✅" palette="forest"  sub={`${successPct}% of total`} delay={0.05} />
        <StatCard label="Success Amount" value={fmt(r.success_amount)}  icon="💰" palette="emerald" sub="Value processed"           delay={0.08} />
        <StatCard label="Failed Count"   value={fmtN(r.failed_count)}   icon="❌" palette="rose"    sub={`${failPct}% of total`}   delay={0.11} />
        <StatCard label="Failed Amount"  value={fmt(r.failed_amount)}   icon="🔴" palette="coral"   sub="Value rejected"           delay={0.14} />
      </div>
      <SectionHead color="#534ab7" delay={0.10}>Success Rate</SectionHead>
      <div className="mq-card" style={{ padding: '22px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Overall Success Rate</span>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700, color: '#1d9e75' }}>{successPct}%</span>
        </div>
        <div style={{ height: 12, background: '#f0f0f0', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${successPct}%`, background: 'linear-gradient(90deg,#0f6e56,#1d9e75)', borderRadius: 999, transition: 'width .8s' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
          <span style={{ fontSize: 10, color: '#aaa' }}>0%</span>
          <span style={{ fontSize: 10, color: '#aaa' }}>100%</span>
        </div>
        <div style={{ display: 'flex', gap: 20, marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1d9e75' }} />
            <span style={{ fontSize: 11, color: '#555' }}>Success: <strong>{fmtN(r.success_count)}</strong> txns</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#a32d2d' }} />
            <span style={{ fontSize: 11, color: '#555' }}>Failed: <strong>{fmtN(r.failed_count)}</strong> txns</span>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <a href="/recharge_reports" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, background: 'linear-gradient(135deg,#27500a,#639922)', color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none', boxShadow: '0 4px 14px rgba(99,153,34,.3)' }}>
          📄 Recharge Reports
        </a>
      </div>
    </div>
  );
}

function CommissionTab({ d, chartReady }) {
  const s        = d?.summary || {};
  const services = d?.services || [];
  return (
    <div className="mq-tab-content">
      <SectionHead color="#534ab7" delay={0}>Commission Summary</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 26 }}>
        <StatCard label="Commission CR"  value={fmt(s.total_in_commission)}  icon="📥" palette="emerald"  sub="Total credited"    delay={0.05} />
        <StatCard label="Commission DR"  value={fmt(s.total_out_commission)} icon="📤" palette="rose"     sub="Total debited"     delay={0.08} />
        <StatCard label="Net Commission" value={fmt(s.total_net_commission)} icon="📈" palette="violet"   sub="Net earned today"  delay={0.11} />
        <StatCard label="Net Charge"     value={fmt(s.total_net_charge)}     icon="📊" palette="sky"      sub="Net charges today" delay={0.14} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 26 }}>
        <div className="mq-card mq-fade-up" style={{ padding: '20px 22px', animationDelay: '.12s' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>Commission Split</div>
          <div style={{ fontSize: 10, color: '#aaa', marginBottom: 16 }}>CR vs DR vs Net</div>
          {chartReady && <DonutChart cr={s.total_in_commission} dr={s.total_out_commission} net={s.total_net_commission} />}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginTop: 14 }}>
            {[
              { label: 'Comm CR',  val: s.total_in_commission,  color: '#1d9e75' },
              { label: 'Comm DR',  val: s.total_out_commission, color: '#a32d2d' },
              { label: 'Net Comm', val: s.total_net_commission, color: '#534ab7' },
            ].map((item) => (
              <span key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#555', fontWeight: 500 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                {item.label}: <span style={{ fontFamily: "'JetBrains Mono', monospace", color: item.color }}>{fmt(item.val)}</span>
              </span>
            ))}
          </div>
        </div>
        <div className="mq-card mq-fade-up" style={{ padding: '20px 22px', animationDelay: '.16s' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', marginBottom: 16 }}>Per-Service Commission</div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {services.map((item, i) => {
              const color = getColor(item.service);
              return (
                <div key={i} className="mq-comm-row" style={{ borderRadius: i === 0 ? '8px 8px 0 0' : i === services.length - 1 ? '0 0 8px 8px' : 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#333' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
                    {item.service}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#1d9e75', fontWeight: 600 }}>{fmt(item.net_commission)}</span>
                </div>
              );
            })}
            {services.length === 0 && <div style={{ textAlign: 'center', color: '#bbb', padding: '20px 0', fontSize: 12 }}>No data</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// SettingCard and SettingsTab — identical to original, no changes needed
function SettingCard({ icon, label, desc, href, palette, delay = 0, badge }) {
  const PALETTES = {
    emerald:  { grad: 'linear-gradient(135deg,#0f6e56,#1d9e75)', shadow: 'rgba(29,158,117,.28)',  iconBg: '#e1f5ee', iconColor: '#0f6e56'  },
    sapphire: { grad: 'linear-gradient(135deg,#0c447c,#185fa5)', shadow: 'rgba(24,95,165,.28)',   iconBg: '#e6f1fb', iconColor: '#0c447c'  },
    violet:   { grad: 'linear-gradient(135deg,#3c3489,#534ab7)', shadow: 'rgba(83,74,183,.28)',   iconBg: '#eeedfe', iconColor: '#3c3489'  },
    amber:    { grad: 'linear-gradient(135deg,#854f0b,#ba7517)', shadow: 'rgba(186,117,23,.28)',  iconBg: '#faeeda', iconColor: '#633806'  },
    coral:    { grad: 'linear-gradient(135deg,#993c1d,#d85a30)', shadow: 'rgba(216,90,48,.28)',   iconBg: '#faece7', iconColor: '#993c1d'  },
    forest:   { grad: 'linear-gradient(135deg,#27500a,#639922)', shadow: 'rgba(99,153,34,.28)',   iconBg: '#eaf3de', iconColor: '#27500a'  },
    rose:     { grad: 'linear-gradient(135deg,#791f1f,#a32d2d)', shadow: 'rgba(163,45,45,.28)',   iconBg: '#fcebeb', iconColor: '#791f1f'  },
    sky:      { grad: 'linear-gradient(135deg,#042c53,#378add)', shadow: 'rgba(55,138,221,.28)',  iconBg: '#e6f1fb', iconColor: '#042c53'  },
    pink:     { grad: 'linear-gradient(135deg,#72243e,#993556)', shadow: 'rgba(153,53,86,.28)',   iconBg: '#fbeaf0', iconColor: '#72243e'  },
  };
  const p = PALETTES[palette] || PALETTES.emerald;
  return (
    <a href={href} className="mq-fade-up" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '18px 18px 16px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 14px rgba(0,0,0,.06)', textDecoration: 'none', color: 'inherit', border: '1px solid rgba(0,0,0,.05)', position: 'relative', overflow: 'hidden', transition: 'transform .2s, box-shadow .2s', animationDelay: `${delay}s` }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 10px 28px ${p.shadow}`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,0,0,.06)'; }}
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: p.grad, borderRadius: '16px 16px 0 0' }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 4 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: p.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>{icon}</div>
        {badge && <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 999, background: p.iconBg, color: p.iconColor, textTransform: 'uppercase', letterSpacing: '.08em', flexShrink: 0 }}>{badge}</span>}
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 11, color: '#999', lineHeight: 1.5 }}>{desc}</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: p.iconColor, display: 'flex', alignItems: 'center', gap: 4 }}>Open →</div>
    </a>
  );
}

function SettingsTab({ roleId, maintenance, onToggle }) {
  const isAdmin    = roleId === 6 || roleId === 1;
  const isWL       = roleId === 2;
  const isDist     = roleId === 3 || roleId === 4;
  const canResource = isAdmin || isWL;
  const canMember   = isAdmin || isWL || isDist;
  const canCompany  = isAdmin;
  const canService  = isAdmin;
  const canPlatformUser = isAdmin || isWL;
  const active = maintenance.is_active;
  return (
    <div className="mq-tab-content">
      {canResource && (<>
        <SectionHead color="#534ab7" delay={0}>Resource Management</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <SettingCard icon="📋" label="Scheme Manager"       desc="Create and manage commission schemes"       href="/scheme_management"          palette="violet"   delay={0.05} />
          <SettingCard icon="💹" label="Commission Manager"   desc="Set up and edit commission structures"     href="/commission_management"      palette="emerald"  delay={0.08} />
          <SettingCard icon="🏷️" label="Commission Types"    desc="Define categories for commission rules"    href="/commission_type_management"  palette="sapphire" delay={0.11} />
          <SettingCard icon="📊" label="Charge Slab Manager" desc="Configure charge slabs per service"        href="/charge_slab_management"     palette="amber"    delay={0.14} />
          {isAdmin && <SettingCard icon="🔌" label="Provider Manager"   desc="Add and manage service providers"          href="/provider_management"        palette="coral"    delay={0.17} />}
          {isAdmin && <SettingCard icon="🔗" label="API Manager"        desc="Manage external API integrations"          href="/api_management"             palette="sky"      delay={0.20} />}
        </div>
      </>)}
      {canMember && (<>
        <SectionHead color="#1d9e75" delay={0.10}>Member Management</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <SettingCard icon="👥" label="Member Manager"      desc="View, edit and manage all members"          href="/member_management"          palette="emerald"  delay={0.12} />
          {(isAdmin || isWL) && <>
            <SettingCard icon="🗑️" label="Deleted Members"  desc="View and restore deleted member accounts"  href="/deleted_member_management"   palette="rose"     delay={0.15} />
            <SettingCard icon="🔒" label="Blocked Members"  desc="Manage security-blocked accounts"          href="/security_block_management"   palette="coral"    delay={0.18} />
          </>}
        </div>
      </>)}
      {canPlatformUser && (<>
        <SectionHead color="#ba7517" delay={0.20}>Platform Users</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <SettingCard icon="🪪" label="Platform User Manager" desc="Manage sub-admin users and their module access" href="/platform_user_management" palette="amber" delay={0.22} badge="RBAC" />
        </div>
      </>)}
      {canCompany && (<>
        <SectionHead color="#185fa5" delay={0.28}>Company & Branding</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <SettingCard icon="🏢" label="Company Manager"  desc="Update company details and config"    href="/company_management" palette="sapphire" delay={0.30} />
          <SettingCard icon="🖼️" label="Banner Manager"  desc="Manage promotional banners"           href="/banner_management"  palette="sky"      delay={0.33} />
          <SettingCard icon="📰" label="News Manager"    desc="Post updates and announcements"       href="/news_section"        palette="forest"   delay={0.36} />
        </div>
      </>)}
      {canService && (<>
        <SectionHead color="#639922" delay={0.38}>Service Configuration</SectionHead>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <SettingCard icon="📦" label="Plan Manager"              desc="Create and manage service plans"              href="/plan_manager"                    palette="forest"   delay={0.40} />
          <SettingCard icon="💰" label="Plan Service Charge"       desc="Set charges per plan per service"             href="/plan_service_charge"             palette="amber"    delay={0.43} />
          <SettingCard icon="🏪" label="Retailer Plan Assign"      desc="Assign plans to retailer accounts"            href="/retailer_plan_page"              palette="emerald"  delay={0.46} />
          <SettingCard icon="🚫" label="Provider Restriction"      desc="Restrict providers at a platform level"       href="/main_provider_restriction"       palette="rose"     delay={0.49} />
          <SettingCard icon="👤" label="User-Based Restriction"    desc="Apply provider limits per individual user"    href="/user_based_provider_restriction" palette="coral"    delay={0.52} />
        </div>
      </>)}
      <SectionHead color="#888" delay={0.55}>Profile & Account</SectionHead>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        <SettingCard icon="🙍" label="View Profile"           desc="Update your personal profile settings"          href="/member_setting_management"   palette="sapphire" delay={0.57} />
        <SettingCard icon="📶" label="Recharge & BBPS Comm."  desc="Your commission config for recharge/BBPS"       href="/user_commission"             palette="forest"   delay={0.60} />
        <SettingCard icon="🔧" label="Other Service Comm."    desc="Commission settings for all other services"     href="/user_other_commission"       palette="violet"   delay={0.63} />
        {isAdmin && <>
          <SettingCard icon="📄" label="API Documentation"       desc="Explore and test the developer API"             href="/api_docs"                    palette="sky"   delay={0.66} badge="DEV" />
          <SettingCard icon="🔗" label="API Integration Request" desc="Submit a new external API integration request"  href="/create_api_integration_request" palette="pink" delay={0.69} />
        </>}
        <SettingCard icon="🎫" label="Support Ticket" desc="Raise and track support issues" href="/ticket_management" palette="amber" delay={0.72} />
      </div>
      {isAdmin && (<>
        <SectionHead color="#a32d2d" delay={0.75}>System Control</SectionHead>
        <div className="mq-card mq-fade-up" style={{ padding: '22px 24px', animationDelay: '.76s' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>🔧 Maintenance Mode</div>
              <div style={{ fontSize: 12, color: '#888' }}>Enabling this will restrict all user access to the platform</div>
            </div>
            <div style={{ padding: '8px 18px', borderRadius: 10, fontWeight: 700, fontSize: 13, background: active ? '#fcebeb' : '#e1f5ee', color: active ? '#791f1f' : '#0f6e56', border: active ? '1.5px solid #f09595' : '1.5px solid #5dcaa5' }}>
              {active ? '● ACTIVE' : '○ INACTIVE'}
            </div>
          </div>
          <button onClick={() => onToggle(!active)} style={{ padding: '10px 24px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, background: active ? 'linear-gradient(135deg,#791f1f,#a32d2d)' : 'linear-gradient(135deg,#0c447c,#185fa5)', color: '#fff', border: 'none', boxShadow: active ? '0 4px 14px rgba(163,45,45,.3)' : '0 4px 14px rgba(24,95,165,.3)', transition: 'all .2s' }}>
            {active ? '🔓 Disable Maintenance' : '🔒 Enable Maintenance'}
          </button>
        </div>
      </>)}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [currentTime, setCurrentTime]     = useState(new Date());
  const [chartReady, setChartReady]       = useState(false);
  const [activeTab, setActiveTab]         = useState('overview');
  const [maintenance, setMaintenance]     = useState({ is_active: false, message: '' });
  const { userid } = useSession();

  useEffect(() => {
    fetchWrapper('https://pro.mokshiq.in/check_maitenance_mode', { method: 'GET' })
      .then(r => { if (r && typeof r.is_active !== 'undefined') setMaintenance({ is_active: r.is_active, message: r.message || '' }); })
      .catch(err => console.error('Maintenance check failed:', err));
  }, []);

  const toggleMaintenance = async (active) => {
    const body = new FormData();
    body.append('maintenance_mode', active ? 'active' : 'inactive');
    try {
      await fetchWrapper('https://pro.mokshiq.in/set_maitenance_mode', { method: 'POST', body });
      setMaintenance(prev => ({ ...prev, is_active: active }));
    } catch (err) { console.error('Toggle failed:', err); }
  };

  useEffect(() => {
    const id = 'mq-admin-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = GLOBAL_CSS;
      document.head.appendChild(el);
    }
    if (!window.Chart) {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js';
      s.onload = () => setChartReady(true);
      document.head.appendChild(s);
    } else { setChartReady(true); }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // ── API now returns role_id, wallets{}, downline_wallets{}, member_stats{},
        //    txn_today{}, pending_load_requests, summary{}, services[], recharge{}
        const response = await fetchWrapper(`https://pro.mokshiq.in/dashboard_api?id=${userid}`);
        setDashboardData(response);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    };
    load();
  }, [userid]);

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#f5f6f8', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
      </div>
    );
  }
  if (error) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#a32d2d', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>⚠️ Failed to load dashboard: {error}</div>;
  }

  const d        = dashboardData;
  const roleId   = d?.role_id;
  const meta     = getRoleMeta(roleId);

  // ── Recharge: API sends d.recharge with keys: success_count, success_amount, failed_count, failed_amount
  //    (same shape old + new API — no change needed)
  const recharge = d?.recharge || {};

  const visibleTabs = ALL_TABS.filter(t => t.roles === null || t.roles.includes(roleId));
  const validTab    = visibleTabs.find(t => t.id === activeTab) ? activeTab : visibleTabs[0]?.id;

  return (
    <main className="mq-dash" style={{ background: '#f0f2f5', marginTop: '25px', minHeight: '100vh', padding: '20px 16px' }}>

      {/* ── Header ── */}
      <div className="mq-fade-up" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, paddingBottom: 18, borderBottom: '1px solid rgba(0,0,0,.07)', position: 'relative', overflow: 'hidden' }}>
        <div className="mq-header-blob" style={{ width: 200, height: 200, background: '#1d9e75', top: -80, left: -60 }} />
        <div className="mq-header-blob" style={{ width: 160, height: 160, background: '#534ab7', top: -60, right: 100 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, position: 'relative' }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#0f6e56,#1d9e75)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 20, boxShadow: '0 4px 14px rgba(29,158,117,.4)' }}>⚡</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-.3px' }}>Today's Overview</div>
            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: '.1em', marginTop: 2 }}>
              {/* Show role name from API if available, else fallback to meta.label */}
              {d?.role_name || meta.label} Dashboard
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          <div className="mq-role-badge" style={{ background: meta.bg, color: meta.color, borderColor: meta.color + '55' }}>
            <span style={{ fontSize: 14 }}>{meta.icon}</span>
            {d?.role_name || meta.label}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: '#0f6e56', fontWeight: 600, background: '#e1f5ee', border: '1px solid #9fe1cb', borderRadius: 10, padding: '6px 14px' }}>
            <div className="mq-live-dot" /> Live
          </div>
          <div style={{ fontSize: 11, color: '#555', fontWeight: 500, background: '#fff', border: '1px solid rgba(0,0,0,.09)', borderRadius: 10, padding: '6px 14px', boxShadow: '0 2px 8px rgba(0,0,0,.05)' }}>
            {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="mq-tab-nav mq-fade-up" style={{ animationDelay: '.08s' }}>
        {visibleTabs.map((tab) => (
          <button key={tab.id} className={`mq-tab-btn${validTab === tab.id ? ' active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            <span className="mq-tab-icon">{tab.icon}</span>
            {tab.label}
            {/* Pending badge on Wallet tab */}
            {tab.id === 'wallet' && d?.pending_load_requests > 0 && (
              <span style={{ background: '#a32d2d', color: '#fff', borderRadius: 999, fontSize: 9, fontWeight: 700, padding: '1px 6px', marginLeft: 2 }}>
                {d.pending_load_requests}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      {validTab === 'overview'   && <OverviewTab d={d} recharge={recharge} chartReady={chartReady} currentTime={currentTime} maintenance={maintenance} onToggleMaintenance={toggleMaintenance} roleId={roleId} />}
      {validTab === 'wallet'     && <WalletTab d={d} roleId={roleId} />}
      {validTab === 'services'   && <ServicesTab d={d} />}
      {validTab === 'recharge'   && <RechargeTab recharge={recharge} chartReady={chartReady} />}
      {validTab === 'commission' && <CommissionTab d={d} chartReady={chartReady} />}
      {validTab === 'settings'   && <SettingsTab roleId={roleId} maintenance={maintenance} onToggle={toggleMaintenance} />}

    </main>
  );
}

export default AdminDashboard;